import { axiosInstance } from "../App"
import { FavoriteStudyDTO } from "../interface/interface"

const DEBUG_FAVORITE_STUDIES = false

function debugFavoriteStudies(message: string, payload: unknown): void {
  if (DEBUG_FAVORITE_STUDIES) {
    console.debug(message, payload)
  }
}


type UnknownRecord = Record<string, unknown>

function asRecord(value: unknown): UnknownRecord {
  if (value && typeof value === "object") {
    return value as UnknownRecord
  }
  return {}
}

export interface FullFavouriteStudyItemDTO {
  favouritestudyitem: number | null
  code: string | null
  description: string | null
  nomenclatorid: number | null
  nomenclatorcode: string | null
  nomenclatordescription: string | null
}

export interface FullFavouriteStudyDTO {
  favouritestudy: number | null
  favouritestudydesc: string
  studytypeid: number | null
  studytype: string | null
  active: boolean
  ispublic: boolean
  usedInAppointments: boolean
  usedInStudyOrders: boolean
  items: FullFavouriteStudyItemDTO[]
}

export interface FavouriteStudyEditFormState {
  favouritestudy: number
  favouritestudydesc: string
  studytype: number
  active: boolean
  ispublic: boolean
  usedInAppointments: boolean
  usedInStudyOrders: boolean
}

export interface FavouriteStudyFormItemState {
  favouritestudyitem: number | null
  code: string | null
  description: string | null
  nomenclatorid: number | null
  nomenclatorcode: string | null
  nomenclatordescription: string | null
}

export async function getAllFavoriteStudies(offSet: number, pageSize: number, params?: unknown): Promise<FavoriteStudyDTO[]> {
  const safeParams = asRecord(params)
  return axiosInstance.post("/api/secured/favouritestudies/loadfavoutitestudieslist", {
        offSet, 
        pageSize,
        sortingFieldInfoList: safeParams.multiSortMeta,
        filters: safeParams.filterDTO, 
      },
    ).then(res => res.data.data)
}

export interface SaveFullFavouriteStudyPayloadItem {
  favouriteStudyItem: number | null
  code: string | null
  description: string | null
  favouriteStudyNomenclator: { nomenclator: number } | null
}

export interface SaveFullFavouriteStudyPayload {
  favouriteStudy: number | null
  studyType: { studyType: number }
  description: string
  active: boolean
  ispublic: boolean
  usedInAppointments: boolean
  usedInStudyOrders: boolean
  favouriteStudiesItem: SaveFullFavouriteStudyPayloadItem[]
  favouriteStudiesItemToRemove?: number[] | null
  favoriteStudiesItemToRemove?: number[] | null
  favouriteStudiesItemToDelete?: number[] | null
  favoriteStudiesItemToDelete?: number[] | null
}

export interface SaveFullFavouriteStudyResponseEnvelope {
  data?: FullFavouriteStudyDTO
}

export interface ApiErrorInfo {
  status: number | null
  data: unknown
  code: string | null
  message: string
  url: string | null
  method: string | null
  hasResponse: boolean
}

function toSafeString(value: unknown): string {
  if (typeof value === "string") {
    return value
  }
  if (value === null || value === undefined) {
    return ""
  }
  return String(value)
}

function toSafeBoolean(value: unknown, fallback = false): boolean {
  if (typeof value === "boolean") {
    return value
  }
  return fallback
}

function toSafeNumber(value: unknown, fallback = 0): number {
  const n = Number(value)
  if (Number.isFinite(n)) {
    return n
  }
  return fallback
}

export function resolveFavouriteStudyId(entity: unknown): number | null {
  const value = asRecord(entity)
  const candidates = [
    value.favouritestudy,
    value.favouriteStudy,
    value.id,
    value.favoriteStudyId
  ]

  for (const value of candidates) {
    const n = Number(value)
    if (Number.isInteger(n) && n > 0) {
      return n
    }
  }

  return null
}

function normalizeFullFavouriteStudyItemDTO(rawItem: unknown): FullFavouriteStudyItemDTO {
  const item = asRecord(rawItem)
  const nestedNomenclator =
    item.favouriteStudyNomenclator ??
    item.favoriteStudyNomenclator ??
    null
  const nomenclator = asRecord(nestedNomenclator)

  return {
    favouritestudyitem: toSafeNumber(
      item.favouritestudyitem ?? item.favouriteStudyItem ?? item.favoriteStudyItem ?? item.id ?? null,
      0
    ) || null,
    code: (item.code as string | null) ?? null,
    description: (item.description as string | null) ?? null,
    nomenclatorid: toSafeNumber(
      item.nomenclatorid ?? item.nomenclatorId ?? nomenclator.nomenclator ?? nomenclator.id ?? null,
      0
    ) || null,
    nomenclatorcode: (item.nomenclatorcode as string | null) ?? (item.nomenclatorCode as string | null) ?? (nomenclator.code as string | null) ?? null,
    nomenclatordescription:
      (item.nomenclatordescription as string | null) ??
      (item.nomenclatorDescription as string | null) ??
      (nomenclator.description as string | null) ??
      null
  }
}

export function normalizeFullFavouriteStudyDto(raw: unknown, fallbackId?: number): FullFavouriteStudyDTO {
  const dto = asRecord(raw)
  const resolvedId = resolveFavouriteStudyId(raw) ?? (fallbackId && fallbackId > 0 ? fallbackId : null)
  const itemsRaw = dto.items ?? dto.favouriteStudiesItem ?? dto.favoriteStudiesItem ?? []
  const normalizedItems = Array.isArray(itemsRaw) ? itemsRaw.map(normalizeFullFavouriteStudyItemDTO) : []

  const studyTypeId =
    dto.studytypeid ??
    dto.studyTypeId ??
    dto.studytype ??
    asRecord(dto.studyType).studyType ??
    dto.studyType ??
    null

  return {
    favouritestudy: resolvedId,
    favouritestudydesc: toSafeString(dto.favouritestudydesc ?? dto.description ?? ""),
    studytypeid: toSafeNumber(studyTypeId, 0) || null,
    studytype: (dto.studytype as string | null) ?? (dto.studyType as string | null) ?? null,
    active: toSafeBoolean(dto.active, true),
    ispublic: toSafeBoolean(dto.ispublic ?? dto.isPublic, false),
    usedInAppointments: toSafeBoolean(dto.usedInAppointments, false),
    usedInStudyOrders: toSafeBoolean(dto.usedInStudyOrders, false),
    items: normalizedItems
  }
}


export function mapFullFavouriteStudyToForm(dto: FullFavouriteStudyDTO): {
  form: FavouriteStudyEditFormState
  items: FavouriteStudyFormItemState[]
} {
  const rawDto = asRecord(dto)
  const safeItemsRaw = rawDto.items ?? rawDto.favouriteStudiesItem ?? []
  const safeItems = Array.isArray(safeItemsRaw)
    ? safeItemsRaw.map((item) => normalizeFullFavouriteStudyItemDTO(item))
    : []
  const resolvedId = resolveFavouriteStudyId(dto) ?? 0
  const resolvedDescription = toSafeString(rawDto.favouritestudydesc ?? rawDto.description ?? "")
  const resolvedStudyTypeId = toSafeNumber(rawDto.studytypeid ?? rawDto.studyTypeId ?? 0, 0)

  return {
    form: {
      favouritestudy: resolvedId,
      favouritestudydesc: resolvedDescription,
      studytype: resolvedStudyTypeId,
      active: toSafeBoolean(rawDto.active, true),
      ispublic: toSafeBoolean(rawDto.ispublic ?? rawDto.isPublic, false),
      usedInAppointments: toSafeBoolean(rawDto.usedInAppointments, false),
      usedInStudyOrders: toSafeBoolean(rawDto.usedInStudyOrders, false)
    },
    items: safeItems.map((item) => ({
      favouritestudyitem: item.favouritestudyitem,
      code: item.code ?? item.nomenclatorcode ?? null,
      description: item.description ?? item.nomenclatordescription ?? null,
      nomenclatorid: item.nomenclatorid ?? null,
      nomenclatorcode: item.nomenclatorcode ?? null,
      nomenclatordescription: item.nomenclatordescription ?? null
    }))
  }
}

export function getApiErrorMessage(err: unknown, fallback: string): string {
  const details = getApiErrorInfo(err)
  const data = asRecord(details.data)
  return (
    (typeof details.data === "string" ? details.data : undefined) ||
    (data.message as string | undefined) ||
    (data.error as string | undefined) ||
    (data.detail as string | undefined) ||
    (details.message || undefined) ||
    fallback
  )
}

export function getApiErrorInfo(err: unknown): ApiErrorInfo {
  const error = asRecord(err)
  const response = asRecord(error.response)
  const config = asRecord(error.config)
  const parsedStatus = Number(response.status)
  const status = Number.isFinite(parsedStatus) ? parsedStatus : null
  const data = response.data
  const code = typeof error.code === "string" ? error.code : null
  const message = typeof error.message === "string" ? error.message : "Unknown error"
  const url = typeof config.url === "string" ? config.url : null
  const method = typeof config.method === "string" ? config.method.toUpperCase() : null
  const hasResponse = status !== null || data !== undefined

  return {
    status,
    data,
    code,
    message,
    url,
    method,
    hasResponse
  }
}

export function toSaveFullFavouriteStudyPayload(
  form: FavouriteStudyEditFormState,
  items: FavouriteStudyFormItemState[],
  removedItemIds: number[] = []
): SaveFullFavouriteStudyPayload {
  const uniqueRemoved = Array.from(new Set((removedItemIds ?? []).filter((id) => Number(id) > 0).map((id) => Number(id))))
  const toRemoveOrNull = uniqueRemoved.length ? uniqueRemoved : null
  return {
    favouriteStudy: form.favouritestudy > 0 ? form.favouritestudy : null,
    studyType: { studyType: toSafeNumber(form.studytype, 0) },
    description: toSafeString(form.favouritestudydesc).trim(),
    active: Boolean(form.active),
    ispublic: Boolean(form.ispublic),
    usedInAppointments: Boolean(form.usedInAppointments),
    usedInStudyOrders: Boolean(form.usedInStudyOrders),
    favouriteStudiesItem: (Array.isArray(items) ? items : []).map((item) => ({
      favouriteStudyItem:
        item.favouritestudyitem && item.favouritestudyitem > 0 ? item.favouritestudyitem : null,
      code: item.nomenclatorid ? null : item.code ?? null,
      description: item.nomenclatorid ? null : item.description ?? null,
      favouriteStudyNomenclator:
        item.nomenclatorid && Number(item.nomenclatorid) > 0
          ? { nomenclator: Number(item.nomenclatorid) }
          : null
    })),
    favouriteStudiesItemToRemove: toRemoveOrNull,
    favoriteStudiesItemToRemove: toRemoveOrNull,
    favouriteStudiesItemToDelete: toRemoveOrNull,
    favoriteStudiesItemToDelete: toRemoveOrNull
  }
}

export function createFavoriteStudy(study: FavoriteStudyDTO): Promise<FavoriteStudyDTO> {
  return axiosInstance
    .post("/api/secured/favouritestudies/createfavoritestudy", study)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error al crear estudio favorito", err)
      throw err
    })
}

export function updateFavoriteStudy(study: FavoriteStudyDTO): Promise<FavoriteStudyDTO> {
  return axiosInstance
    .put("/api/secured/favouritestudies/updatefavoritestudy", study)
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error al actualizar estudio favorito", err)
      throw err
    })
}

export function deleteFavoriteStudy(id: number): Promise<void> {
  return axiosInstance
    .delete("/api/secured/favouritestudies/deletefavoritestudy", { params: { id } })
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error al eliminar estudio favorito", err)
      throw err
    })
}

export function getFavoriteStudyById(id: number): Promise<FavoriteStudyDTO> {
  return axiosInstance
    .get("/api/secured/favouritestudies/getById", { params: { id } })
    .then((res) => res.data)
    .catch((err) => {
      console.error("Error al obtener estudio favorito", err)
      throw err
    })
}

export async function getFullFavoriteStudyById(id: number): Promise<FullFavouriteStudyDTO> {
  debugFavoriteStudies("FavoriteStudy GET id:", id)
  return axiosInstance
    .get("/api/secured/favouritestudies/getFullFavoriteStudyById", { params: { id } })
    .then((res) => {
      const payload = res.data?.data ?? res.data
      debugFavoriteStudies("FavoriteStudy GET raw response:", payload)
      return normalizeFullFavouriteStudyDto(payload, id)
    })
    .catch((err: unknown) => {
      const status = Number(asRecord(asRecord(err).response).status) || 0
      const fallback =
        status === 404
          ? `No se encontro el estudio favorito con id ${id}.`
          : "No se pudo cargar el detalle del estudio favorito."
      throw new Error(getApiErrorMessage(err, fallback))
    })
}

export async function getFullFavoriteStudiesByIds(ids: number[]): Promise<FullFavouriteStudyDTO[]> {
  const uniqueIds = Array.from(
    new Set((Array.isArray(ids) ? ids : []).map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0))
  )

  if (!uniqueIds.length) return []

  const query = new URLSearchParams()
  uniqueIds.forEach((id) => query.append("ids", String(id)))

  return axiosInstance
    .get(`/api/secured/favouritestudies/getFullFavoriteStudiesByIds?${query.toString()}`)
    .then((res) => {
      const payload = res.data?.data ?? res.data
      const list = Array.isArray(payload) ? payload : []
      return list.map((raw) => normalizeFullFavouriteStudyDto(raw))
    })
}

export async function getStudyTypes() {
  return axiosInstance.get("/api/secured/favouritestudies/studytypes")
    .then(r => r.data)
}

export async function getNomenclators(filter?: string) {
  return axiosInstance.get("/api/secured/favouritestudies/getNomenclators", { params: { filter } })
    .then(r => r.data)
}

export async function saveFullFavouriteStudy(favouriteStudy: SaveFullFavouriteStudyPayload): Promise<FullFavouriteStudyDTO> {
  try {
    const res = await axiosInstance.post<FullFavouriteStudyDTO | SaveFullFavouriteStudyResponseEnvelope>(
      "/api/secured/favouritestudies/saveFullFavouriteStudy",
      favouriteStudy
    )
    const payload = asRecord(res.data).data ?? res.data
    return normalizeFullFavouriteStudyDto(payload, favouriteStudy.favouriteStudy ?? undefined)
  } catch (err: unknown) {
    const details = getApiErrorInfo(err)
    const logPayload: Record<string, unknown> = {
      status: details.status,
      data: details.data,
      code: details.code,
      message: details.message
    }

    if (!details.hasResponse) {
      logPayload.url = details.url
      logPayload.method = details.method
    }

    console.error("Error al guardar estudio favorito completo", logPayload)
    throw err
  }
}

export async function findFavoriteStudyByDescription(
  description: string,
  studyType: number
): Promise<FavoriteStudyDTO | null> {
  const normalizedDescription = description.trim().toLowerCase()
  const list = await getAllFavoriteStudies(0, 200)
  const matches = list.filter((item) => {
    const itemDescription = item.favouritestudydesc?.trim().toLowerCase()
    const itemStudyType = Number(item.studytype)
    return itemDescription === normalizedDescription && itemStudyType === Number(studyType)
  })

  if (!matches.length) {
    return null
  }

  return matches.sort((a, b) => Number(b.favouritestudy) - Number(a.favouritestudy))[0]
}

export function toggleFavoriteStudy(id: number): Promise<void> {
  return axiosInstance
    .put("/api/secured/favouritestudies/toggleFavouriteStudy", null, { params: { id } })
    .then(res => res.data)
}
